import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import moment from 'moment';
import styled from 'styled-components';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { MdOutlineCircle } from 'react-icons/md';
import { StyledModalBody } from '../../../../components/Modals';
import { ButtonBodyVariant } from '../../../../components/ui/ButtonVariant';
import { Flex } from '../../../../components/ui/Flex';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../../components/ui/Form';
import { CustomDatePicker } from '../../../../components/CustomSearchBar';
import { StyledExportCSVButton } from '../../../../components/tables/TableComponents';
import { closeActiveModal } from '../../../../helper/modals';
import { DATE_FORMATS } from '../../../users/constant';
import { Fonts, Palette } from '../../../../components/utils';
import { setToast } from '../../../../redux/actions/window';
import {
  buildDateRangePaymentsCsvFilename,
  buildWholeMonthPaymentsCsvFilename,
  fetchBuildingPaymentsCsvExport,
  getLocalDayRangeUtcIso,
  mapPaymentHistoryRowsToCsvData
} from '../../PaymentUtils';
import {
  DescriptionText,
  ModalButtonsContainer,
  PaymentModal,
  TitleText
} from '../../PaymentComponents';

const EXPORT_OPTION_WHOLE_MONTH = 'wholeMonth';
const EXPORT_OPTION_DATE_RANGE = 'dateRange';

const dateRangeSchema = Yup.object().shape({
  startDate: Yup.mixed().required('A start date is required'),
  endDate: Yup.mixed().required('An end date is required')
});

const PaymentsDownloadCsvModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.headerModal.modal);
  const modalData = useSelector((state) => state.headerModal.data);
  const csvLinkRef = useRef(null);
  const [shouldDownload, setShouldDownload] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pendingDownloadData, setPendingDownloadData] = useState(null);
  const [pendingDownloadFilename, setPendingDownloadFilename] = useState(null);

  const [exportOption, setExportOption] = useState(EXPORT_OPTION_WHOLE_MONTH);

  const csvData = modalData?.csvData ?? [];
  const csvHeaders = modalData?.headers ?? [];
  const activeCsvData = pendingDownloadData ?? csvData;
  const activeCsvFilename =
    pendingDownloadFilename ??
    buildWholeMonthPaymentsCsvFilename(modalData?.selectedMonthDate);

  const form = useForm({
    resolver: yupResolver(dateRangeSchema),
    mode: 'onChange',
    defaultValues: {
      startDate: null,
      endDate: null
    }
  });

  const startDate = useWatch({ control: form.control, name: 'startDate' });
  const endDate = useWatch({ control: form.control, name: 'endDate' });

  const selectedMonth = moment(modalData?.selectedMonthDate);
  const monthLabel = selectedMonth.isValid()
    ? selectedMonth.format('MMMM YYYY')
    : moment().format('MMMM YYYY');

  const disabledStartDates = (current) => {
    if (!current) return false;

    const currentDay = moment(current).startOf('day');
    const today = moment().startOf('day');

    if (currentDay.isAfter(today)) return true;
    if (endDate && currentDay.isAfter(moment(endDate).startOf('day'))) return true;

    return false;
  };

  const disabledEndDates = (current) => {
    if (!current) return false;

    const currentDay = moment(current).startOf('day');
    const today = moment().startOf('day');

    if (currentDay.isAfter(today)) return true;
    if (startDate && currentDay.isBefore(moment(startDate).startOf('day'))) return true;

    return false;
  };

  useEffect(() => {
    if (!shouldDownload || !csvLinkRef.current?.link) return;
    const timer = setTimeout(() => {
      csvLinkRef.current.link.click();
      setShouldDownload(false);
      setPendingDownloadData(null);
      setPendingDownloadFilename(null);
      closeActiveModal();
    }, 0);
    return () => clearTimeout(timer);
  }, [shouldDownload, activeCsvData]);

  const handleClose = () => {
    setExportOption(EXPORT_OPTION_WHOLE_MONTH);
    setPendingDownloadData(null);
    setPendingDownloadFilename(null);
    setDownloading(false);
    form.reset({ startDate: null, endDate: null });
    closeActiveModal();
  };

  const handleWholeMonthNext = () => {
    setPendingDownloadData(null);
    setPendingDownloadFilename(
      buildWholeMonthPaymentsCsvFilename(modalData?.selectedMonthDate)
    );
    setShouldDownload(true);
  };

  const handleDateRangeNext = async (values) => {
    const buildingId = modalData?.buildingId;
    if (!buildingId) {
      dispatch(
        setToast({
          toastShow: true,
          toastMessage: t('Building is required to export payments.'),
          modal: 'errorToast'
        })
      );
      return;
    }

    const { startDate: startIso, endDate: endIso } = getLocalDayRangeUtcIso(
      values.startDate,
      values.endDate
    );

    setDownloading(true);
    try {
      const rows = await fetchBuildingPaymentsCsvExport(buildingId, startIso, endIso);
      const data = mapPaymentHistoryRowsToCsvData(rows, modalData?.buildingName ?? '');
      setPendingDownloadData(data);
      setPendingDownloadFilename(
        buildDateRangePaymentsCsvFilename(values.startDate, values.endDate)
      );
      setShouldDownload(true);
    } catch (_err) {
      dispatch(
        setToast({
          toastShow: true,
          toastMessage: t('Failed to export payments. Please try again.'),
          modal: 'errorToast'
        })
      );
    } finally {
      setDownloading(false);
    }
  };

  const onNextClick = () => {
    if (exportOption === EXPORT_OPTION_WHOLE_MONTH) {
      handleWholeMonthNext();
      return;
    }
    form.handleSubmit(handleDateRangeNext)();
  };

  const isNextDisabled =
    downloading ||
    (exportOption === EXPORT_OPTION_DATE_RANGE && !form.formState.isValid);

  return (
    <>
      <PaymentModal
        show={modal === 'PaymentsDownloadCsvModal'}
        onClose={handleClose}
        closeOnBackdrop={false}
        width="460px">
        <StyledModalBody>
          <Flex direction="column" gap="1rem">
            <TitleText>{t('Download CSV')}</TitleText>

            <Flex direction="column" gap="0.75rem">
              <ExportOptionCard
                selected={exportOption === EXPORT_OPTION_WHOLE_MONTH}
                onClick={() => setExportOption(EXPORT_OPTION_WHOLE_MONTH)}>
                {exportOption === EXPORT_OPTION_WHOLE_MONTH ? (
                  <IoCheckmarkCircle size={22} color={Palette.HAZY_BLUE} style={{ flexShrink: 0 }} />
                ) : (
                  <MdOutlineCircle size={22} color={Palette.HAZY_BLUE} style={{ flexShrink: 0 }} />
                )}
                <Flex direction="column" alignItems="flex-start" gap="0.25rem" style={{ flex: 1 }}>
                  <MonthLabel>{monthLabel}</MonthLabel>
                  <OptionDescription>
                    {t('Export transaction data for {{month}}.', { month: monthLabel })}
                  </OptionDescription>
                </Flex>
              </ExportOptionCard>

              <ExportOptionCard
                selected={exportOption === EXPORT_OPTION_DATE_RANGE}
                onClick={() => setExportOption(EXPORT_OPTION_DATE_RANGE)}>
                {exportOption === EXPORT_OPTION_DATE_RANGE ? (
                  <IoCheckmarkCircle size={22} color={Palette.HAZY_BLUE} style={{ flexShrink: 0 }} />
                ) : (
                  <MdOutlineCircle size={22} color={Palette.HAZY_BLUE} style={{ flexShrink: 0 }} />
                )}
                <Flex direction="column" alignItems="flex-start" gap="0.25rem">
                  <OptionTitle>{t('Select Date Range')}</OptionTitle>
                  <OptionDescription>
                    {t('Choose a custom start and end date to export.')}
                  </OptionDescription>
                </Flex>
              </ExportOptionCard>
            </Flex>

            {exportOption === EXPORT_OPTION_DATE_RANGE && (
              <Form {...form}>
                <Flex direction="row" gap="1rem" alignItems="flex-start" style={{ width: '100%' }}>
                  <Flex direction="column" style={{ flex: 1, minWidth: 0 }}>
                    <FormField
                      control={form.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem alt>
                          <FormLabel v3>{t('Start Date')}</FormLabel>
                          <FormControl>
                            <CustomDatePicker
                              {...field}
                              onOpenChange={(open) => {
                                if (open) {
                                  form.setValue('endDate', null, {
                                    shouldValidate: false,
                                    shouldDirty: true
                                  });
                                  form.clearErrors('endDate');
                                }
                              }}
                              className="form-control"
                              width="100%"
                              height="3rem"
                              showSecond={false}
                              format={DATE_FORMATS.MMMM_DD_YYYY}
                              placeholder={t('Select date')}
                              disabledDate={disabledStartDates}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </Flex>

                  <Flex direction="column" style={{ flex: 1, minWidth: 0 }}>
                    <FormField
                      control={form.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem alt>
                          <FormLabel v3>{t('End Date')}</FormLabel>
                          <FormControl>
                            <CustomDatePicker
                              {...field}
                              className="form-control"
                              width="100%"
                              height="3rem"
                              showSecond={false}
                              format={DATE_FORMATS.MMMM_DD_YYYY}
                              placeholder={t('Select date')}
                              disabledDate={disabledEndDates}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </Flex>
                </Flex>
              </Form>
            )}

            <ModalButtonsContainer gapY="8px">
              <ButtonBodyVariant
                cssOverride="width: 8.875rem; height: 2rem;"
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={downloading}>
                {t('Cancel')}
              </ButtonBodyVariant>
              <ButtonBodyVariant
                cssOverride="width: 8.875rem; height: 2rem;"
                type="button"
                disabled={isNextDisabled}
                onClick={onNextClick}>
                {downloading ? t('Loading…') : t('Next')}
              </ButtonBodyVariant>
            </ModalButtonsContainer>
          </Flex>
        </StyledModalBody>
      </PaymentModal>

      {shouldDownload && (
        <StyledExportCSVButton
          ref={csvLinkRef}
          data={activeCsvData}
          headers={csvHeaders}
          filename={activeCsvFilename}
          target="_blank"
          style={{ display: 'none' }}
        />
      )}
    </>
  );
};

const ExportOptionCard = styled(Flex)`
  min-height: 5.5rem;
  padding: 1.25rem 1.125rem;
  align-items: flex-start;
  gap: 1rem;
  border-radius: 0.625rem;
  border: 1px solid ${Palette.DISABLED};
  background: ${Palette.WHITE};
  cursor: pointer;

  ${({ selected }) =>
    selected &&
    `
    border: 1px solid ${Palette.HAZY_BLUE};
    background: ${Palette.SOLITUDE};
  `}
`;

const MonthLabel = styled.span`
  color: ${Palette.BLACK};
  font-family: ${Fonts.RED_HAT_FONT};
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.5rem;
`;

const OptionTitle = styled(TitleText)`
  font-size: 1rem;
  line-height: 1.5rem;
`;

const OptionDescription = styled(DescriptionText)`
  text-align: left;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${Palette.BLACK_50};
`;

export default PaymentsDownloadCsvModal;
